import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft } from 'lucide-react';
import { saleService } from '@/services/sale.service';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const formatCurrency = (amountInCents: number): string => {
  if (!amountInCents && amountInCents !== 0) return 'R$ 0,00';
  return (amountInCents / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });
};

const formatDateDetail = (dateString: string): string => {
  try {
    if (!dateString) return '-';
    return format(new Date(dateString), "dd/MM/yyyy - HH:mm", { locale: ptBR });
  } catch {
    return dateString || '-';
  }
};

// Extrai o customer de um charge ou do checkout (PagarMe estrutura diferente para checkouts)
const getCustomerFromOrder = (saleDetail: any, charge: any) => {
  // Primeiro tenta pegar do charge (vendas diretas)
  if (charge?.customer?.name) return charge.customer;
  // Depois tenta do order.customer (padrão PagarMe v5)
  if (saleDetail?.customer?.name) return saleDetail.customer;
  // Por último, do checkout (vendas via checkout)
  if (saleDetail?.checkouts?.[0]?.customer?.name) return saleDetail.checkouts[0].customer;
  return charge?.customer || null;
};

export default function RelatorioVendaDetalhe() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [saleDetail, setSaleDetail] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (id && id !== 'null' && id !== 'undefined') {
      loadSaleDetail(id);
    } else {
      setIsLoading(false);
      setLoadError(true);
    }
  }, [id]);

  const loadSaleDetail = async (idpagarme: string) => {
    setIsLoading(true);
    setLoadError(false);
    try {
      const response = await saleService.getSaleDetail(idpagarme);
      if (response && response.id) {
        setSaleDetail(response);
      } else {
        setLoadError(true);
        toast.error('Venda não encontrada na PagarMe');
      }
    } catch (error) {
      console.error('Erro ao buscar detalhes da venda:', error);
      setLoadError(true);
      toast.error('Erro ao buscar detalhes da venda');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <h2 className="text-lg font-semibold">Aguarde</h2>
          <p className="text-muted-foreground">Carregando detalhes...</p>
        </div>
      </div>
    );
  }

  if (!saleDetail || loadError) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">
          {loadError ? 'Não foi possível carregar os detalhes desta venda.' : 'Venda não encontrada.'}
        </p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/relatorios-vendas')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar para vendas
        </Button>
      </div>
    );
  }

  const charges = saleDetail.charges || [];

  return (
    <div className="space-y-6 p-4 md:p-6">
      <Card>
        <CardContent className="p-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h2 className="text-xl font-semibold">Código: {saleDetail.code || '-'}</h2>
              <p className="text-sm text-muted-foreground">ID PagarMe: {saleDetail.id || '-'}</p>
            </div>
            <Button variant="outline" onClick={() => navigate('/relatorios-vendas')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar para todas as vendas
            </Button>
          </div>

          <hr className="my-4" />

          {/* Status, Valor, Data */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 my-6">
            <div>
              <span className="text-sm font-semibold">Status:</span>
              <div className="mt-1">
                {saleDetail.status === 'paid' ? (
                  <Badge className="bg-green-500 text-white">pago</Badge>
                ) : (
                  <Badge variant="destructive">{saleDetail.status || 'desconhecido'}</Badge>
                )}
              </div>
            </div>
            <div>
              <span className="text-sm font-semibold">Valor Total:</span>
              <p className="mt-1 text-lg font-bold">{formatCurrency(saleDetail.amount)}</p>
            </div>
            <div className="sm:text-right">
              <span className="text-sm font-semibold">Data:</span>
              <p className="mt-1">{formatDateDetail(saleDetail.created_at)}</p>
            </div>
          </div>

          <hr className="my-4" />

          {/* Tabela: Dados do Cliente (Revendedora) */}
          {charges.length > 0 && (
            <>
              <h3 className="text-lg font-semibold mb-4">Dados "Cliente" (Revendedora)</h3>
              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm border">
                  <thead>
                    <tr className="bg-muted">
                      <th className="text-left p-3 font-semibold">Nome</th>
                      <th className="text-left p-3 font-semibold">Documento</th>
                      <th className="text-left p-3 font-semibold">E-mail</th>
                      <th className="text-left p-3 font-semibold">Telefones</th>
                      <th className="text-left p-3 font-semibold">Criado em</th>
                    </tr>
                  </thead>
                  <tbody>
                    {charges.map((charge: any, idx: number) => {
                      const customer = getCustomerFromOrder(saleDetail, charge);
                      return (
                        <tr key={idx} className="border-b">
                          <td className="p-3">{customer?.name || '-'}</td>
                          <td className="p-3">
                            {((customer?.document_type || customer?.type || '--').toUpperCase()) + ': ' + (customer?.document || '--')}
                          </td>
                          <td className="p-3">{customer?.email || '-'}</td>
                          <td className="p-3">
                            {customer?.phones?.mobile_phone
                              ? `(${customer.phones.mobile_phone.area_code || ''}) ${customer.phones.mobile_phone.number || ''}`
                              : '-'}
                          </td>
                          <td className="p-3">{formatDateDetail(customer?.created_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <hr className="my-4" />

              {/* Tabela: Dados da Cobrança */}
              <h3 className="text-lg font-semibold mb-4">Dados da Cobrança</h3>
              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm border">
                  <thead>
                    <tr className="bg-muted">
                      <th className="text-left p-3 font-semibold">Valor Pago</th>
                      <th className="text-left p-3 font-semibold">Tipo de Transação</th>
                      <th className="text-left p-3 font-semibold">Parcelas</th>
                      <th className="text-left p-3 font-semibold">Análise Fraude</th>
                      <th className="text-left p-3 font-semibold">Pagamento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {charges.map((charge: any, idx: number) => {
                      const tx = charge.last_transaction;
                      const antifraud = tx?.antifraud_response;
                      return (
                        <tr key={idx} className="border-b">
                          <td className="p-3">{formatCurrency(charge.paid_amount || charge.amount || 0)}</td>
                          <td className="p-3">{tx?.transaction_type || charge.payment_method || '-'}</td>
                          <td className="p-3">
                            {tx?.transaction_type === 'pix' ? '1x' : (tx?.installments || '-')}
                          </td>
                          <td className="p-3">
                            {antifraud
                              ? (antifraud.status || 'S/ dados') + ' | Score: ' + (antifraud.score || 'S/ dados')
                              : 'S/ dados'}
                          </td>
                          <td className="p-3">
                            {tx?.transaction_type === 'credit_card' || tx?.card ? (
                              <span>
                                <em>Cartão: {tx?.card?.brand || '-'}</em>{' | Final '}
                                <strong>{tx?.card?.last_four_digits || '****'}</strong>
                                {' (' + (tx?.card?.exp_month || '') + '/' + (tx?.card?.exp_year || '') + '): ' + (tx?.card?.holder_name || '')}
                              </span>
                            ) : tx?.transaction_type === 'pix' ? (
                              <span>
                                {tx?.payer ? (
                                  <>
                                    Pagador: {tx.payer.name?.substring(0, 15) || '-'}
                                    {tx.payer.bank_account && ` (${tx.payer.bank_account.bank_name})`}
                                    <br />
                                    Documento: {tx.payer.document || '-'}
                                  </>
                                ) : 'Pix (sem dados do pagador)'}
                              </span>
                            ) : (
                              <span>{tx?.transaction_type || '-'}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {charges.length === 0 && (
            <div className="text-center py-6 text-muted-foreground">
              <p>Nenhuma cobrança encontrada para esta venda.</p>
            </div>
          )}

          <hr className="my-4" />

          {/* Tabela: Itens */}
          {saleDetail.items && saleDetail.items.length > 0 && (
            <>
              <h3 className="text-lg font-semibold mb-4">Dados Itens</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border">
                  <thead>
                    <tr className="bg-muted">
                      <th className="text-left p-3 font-semibold">Valor</th>
                      <th className="text-left p-3 font-semibold">Código (Revendedora)</th>
                      <th className="text-left p-3 font-semibold">Descrição</th>
                      <th className="text-left p-3 font-semibold">QNTD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {saleDetail.items.map((item: any, idx: number) => (
                      <tr key={idx} className="border-b">
                        <td className="p-3">{formatCurrency(item.amount || 0)}</td>
                        <td className="p-3">{item.code || '-'}</td>
                        <td className="p-3">{item.description || '-'}</td>
                        <td className="p-3">{item.quantity || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
